// Kept identical in website/src/lib and app/src/lib; tested by marketingTracking.test.ts.
// The shared first-party cookie connects wavegen.ai and app.wavegen.ai.
export const MARKETING_CONSENT_EVENT = "wavegen:marketing-consent";
const CONSENT = "wg_ad_consent";
const ATTRIBUTION = "wg_ad_attribution";
const HANDOFF_CLICK = "wg_fbc";
export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;
const TTL = 30 * 86400;
export type Attribution = {
  capturedAt: number;
  utm: Record<string, string>;
  fbc?: string;
};
let entry: Attribution | undefined;

export function marketingHost(): boolean {
  return (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    ["wavegen.ai", "www.wavegen.ai", "app.wavegen.ai"].includes(
      window.location.hostname
    )
  );
}
export function readCookie(name: string): string {
  try {
    const raw = document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${name}=`));
    return raw ? decodeURIComponent(raw.slice(name.length + 1)) : "";
  } catch {
    return "";
  }
}
function cookie(name: string, value: string, seconds: number): void {
  try {
    const domain = marketingHost() ? "; Domain=wavegen.ai; Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(
      value
    )}; Path=/; Max-Age=${seconds}; SameSite=Lax${domain}`;
  } catch {
    /* Tracking must never interrupt login. */
  }
}
export function marketingConsent(): "granted" | "denied" | "unknown" {
  if (
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl
  )
    return "denied";
  const value = readCookie(CONSENT);
  return value === "granted" || value === "denied" ? value : "unknown";
}
/** Tracking defaults on; a stored opt-out or Global Privacy Control overrides it.
 * This is an eligibility decision, not a record of affirmative user consent.
 */
export function marketingTrackingAllowed(): boolean {
  return marketingHost() && marketingConsent() !== "denied";
}
// Preserve the original Meta click timestamp, with the same 30-day lifetime as attribution.
function validClick(
  value: string | null | undefined,
  now = Date.now()
): string | undefined {
  if (!value || !/^fb\.\d\.\d{13}\.[a-zA-Z0-9_-]{1,500}$/.test(value)) return undefined;
  const timestamp = Number(value.split(".")[2]);
  return timestamp <= now && timestamp > now - TTL * 1000 ? value : undefined;
}
export function attributionFromUrl(
  href: string,
  now = Date.now()
): Attribution | undefined {
  try {
    const url = new URL(href);
    const utm: Record<string, string> = {};
    for (const key of UTM_KEYS) {
      const value = url.searchParams.get(key);
      // Campaign labels only. Never forward arbitrary URL parameters or free-form content.
      if (value && /^[a-zA-Z0-9 _.,:|+~-]{1,150}$/.test(value))
        utm[key] = value;
    }
    const click = url.searchParams.get("fbclid");
    // Only app sign-in routes accept the browser handoff. Never import a
    // browser ID or tracking preference from another browser/device.
    const handedClick =
      url.origin === "https://app.wavegen.ai" &&
      ["/login", "/finish-signin"].includes(url.pathname)
        ? validClick(url.searchParams.get(HANDOFF_CLICK), now)
        : undefined;
    const fbc =
      click && /^[a-zA-Z0-9_-]{1,500}$/.test(click)
        ? `fb.1.${now}.${click}`
        : handedClick;
    return Object.keys(utm).length || fbc
      ? {
          capturedAt: fbc === handedClick && handedClick
            ? Number(handedClick.split(".")[2])
            : now,
          utm,
          ...(fbc ? { fbc } : {}),
        }
      : undefined;
  } catch {
    return undefined;
  }
}
export function captureMarketingEntry(href = window.location.href): void {
  if (!marketingTrackingAllowed()) {
    entry = undefined;
    return;
  }
  const next = attributionFromUrl(href);
  const previous = entry || currentAttribution();
  if (next) {
    // Revisiting the same click must retain its original click timestamp.
    if (
      next.fbc &&
      previous?.fbc &&
      next.fbc.split(".").slice(3).join(".") ===
        previous.fbc.split(".").slice(3).join(".")
    ) {
      next.fbc = previous.fbc;
      next.capturedAt = previous.capturedAt;
    }
    if (
      !next.fbc &&
      previous &&
      UTM_KEYS.every((key) => next.utm[key] === previous.utm[key])
    ) {
      next.fbc = previous.fbc;
      next.capturedAt = previous.capturedAt;
    }
    // An old email must not replace a newer click already captured here.
    entry = next.fbc && previous?.fbc && next.capturedAt < previous.capturedAt
      ? previous
      : next;
  }
  if (marketingTrackingAllowed() && entry)
    cookie(ATTRIBUTION, JSON.stringify(entry), TTL);
}
export function currentAttribution(): Attribution | undefined {
  if (!marketingTrackingAllowed()) return undefined;
  try {
    const value = JSON.parse(
      readCookie(ATTRIBUTION) || "null"
    ) as Attribution | null;
    if (
      value &&
      value.capturedAt <= Date.now() &&
      value.capturedAt > Date.now() - TTL * 1000 &&
      value.utm &&
      typeof value.utm === "object"
    )
      return value;
  } catch {
    /* Missing or corrupt cookies are harmless. */
  }
  return undefined;
}
export function setMarketingConsent(value: "granted" | "denied"): void {
  cookie(CONSENT, value, 180 * 86400);
  if (marketingConsent() === "granted") captureMarketingEntry();
  else {
    cookie(ATTRIBUTION, "", 0);
    cookie("_fbc", "", 0);
    cookie("_fbp", "", 0);
    // Also remove any host-only cookies created by the browser pixel.
    for (const name of ["_fbc", "_fbp"]) {
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;
    }
    entry = undefined;
  }
  window.dispatchEvent(new Event(MARKETING_CONSENT_EVENT));
}
export function signupMarketingContext(): Record<string, unknown> | undefined {
  if (!marketingTrackingAllowed()) return undefined;
  const attribution = currentAttribution();
  return {
    trackingAllowed: true,
    ...(marketingConsent() === "granted" ? { consent: true } : {}),
    utm: attribution?.utm || {},
    fbc: attribution?.fbc || readCookie("_fbc"),
    fbp: readCookie("_fbp"),
  };
}
/** Carry campaign labels and the original click through sign-in browser changes.
 * The destination applies its own preference and keeps its own browser ID.
 */
export function appendMarketingCampaign(params: URLSearchParams): void {
  params.delete(HANDOFF_CLICK);
  if (!marketingTrackingAllowed()) return;
  const attribution = currentAttribution();
  const fbc = validClick(attribution?.fbc || readCookie("_fbc"));
  if (fbc) params.set(HANDOFF_CLICK, fbc);
  for (const key of UTM_KEYS) {
    const value = attribution?.utm[key];
    if (value) params.set(key, value);
  }
}
